import { useCallback, useEffect, useState } from 'react';
import { APP_START_NEW_PROJECT } from '../../../shared/site-control/events';
import { WELCOME_INTRO_DEBUG_ALWAYS_SHOW } from '../../../landing/welcomeIntroSteps';
import {
  clearNewProjectOnboardingIntent,
  markNewProjectOnboardingIntent,
  peekNewProjectOnboardingIntent,
} from '../newProjectOnboardingIntent';

/** @typedef {'idle' | 'welcome' | 'tour' | 'done'} EditorOnboardingPhase */

/**
 * @param {{ startNewProject?: boolean, skipOnboarding?: boolean }} locationState
 * @returns {EditorOnboardingPhase}
 */
function initialPhase(locationState) {
  if (locationState?.skipOnboarding) {
    return 'idle';
  }
  if (locationState?.startNewProject || peekNewProjectOnboardingIntent()) {
    return 'welcome';
  }
  return 'idle';
}

/**
 * Единое UI-состояние обучалки: welcome → tour (5 шагов) → done.
 * Повторный показ только после явного «нового проекта» с лендинга/ЛК (не из меню /app).
 */
export function useEditorOnboardingState({
  locationState,
  isAppLoaded,
  isHydrating,
  locationSearch,
}) {
  const [phase, setPhase] = useState(() => initialPhase(locationState));

  const suppressOnboarding = useCallback(() => {
    clearNewProjectOnboardingIntent();
    setPhase((prev) => {
      if (prev === 'welcome' || prev === 'tour') {
        return 'idle';
      }
      return prev;
    });
  }, []);

  const beginNewProjectOnboarding = useCallback(() => {
    markNewProjectOnboardingIntent();
    setPhase('welcome');
  }, []);

  useEffect(() => {
    if (!locationState?.startNewProject) {
      return;
    }
    if (locationState?.skipOnboarding) {
      suppressOnboarding();
      return;
    }
    beginNewProjectOnboarding();
  }, [
    locationState?.startNewProject,
    locationState?.skipOnboarding,
    beginNewProjectOnboarding,
    suppressOnboarding,
  ]);

  useEffect(() => {
    function handleStartNewProject(event) {
      if (event?.detail?.skipOnboarding) {
        suppressOnboarding();
        return;
      }
      beginNewProjectOnboarding();
    }
    window.addEventListener(APP_START_NEW_PROJECT, handleStartNewProject);
    return () => window.removeEventListener(APP_START_NEW_PROJECT, handleStartNewProject);
  }, [beginNewProjectOnboarding, suppressOnboarding]);

  useEffect(() => {
    if (!isAppLoaded || isHydrating) {
      return;
    }
    const params = new URLSearchParams(locationSearch || '');
    if (params.get('projectId')) {
      setPhase((prev) => {
        if (prev === 'welcome' || prev === 'tour') {
          return 'idle';
        }
        return prev;
      });
    }
  }, [isAppLoaded, isHydrating, locationSearch]);

  useEffect(() => {
    if (WELCOME_INTRO_DEBUG_ALWAYS_SHOW && isAppLoaded && !isHydrating) {
      setPhase((prev) => (prev === 'welcome' ? prev : 'welcome'));
    }
  }, [isAppLoaded, isHydrating]);

  const showWelcomeIntro = phase === 'welcome';
  const showEditorTour = phase === 'tour';

  const openTour = useCallback(() => {
    clearNewProjectOnboardingIntent();
    setPhase('tour');
  }, []);

  const closeOnboarding = useCallback(() => {
    clearNewProjectOnboardingIntent();
    setPhase('done');
  }, []);

  return {
    showWelcomeIntro,
    showEditorTour,
    openTour,
    closeOnboarding,
  };
}
